import { lazy, Suspense, useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import apiClient from "@/lib/api-client"
import { setDatasetInputs, hasDatasetInputs, type DatasetInputs } from "@/features/hierarchy/dataset"
import { FullPageLoader } from "./AuthGuard"

// The whole demonstration (layout, hierarchy, every page) is one lazily imported
// tree, imported only after the jurisdiction dataset is in place: its modules
// derive their records from it when they load.
const DemoApp = lazy(() => import("@/app/DemoApp"))

/** Fetches the account's jurisdiction dataset, then mounts the demonstration. */
export function DatasetGate() {
  const [state, setState] = useState<"loading" | "ready" | "error">(hasDatasetInputs() ? "ready" : "loading")

  useEffect(() => {
    if (state !== "loading") return
    let live = true
    apiClient.get<DatasetInputs>("/jurisdiction/dataset")
      .then((r) => { if (live) { setDatasetInputs(r.data); setState("ready") } })
      .catch(() => { if (live) setState("error") })
    return () => { live = false }
  }, [state])

  if (state === "loading") return <FullPageLoader label="Loading your jurisdiction…" />
  if (state === "error") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-lg font-semibold">The demonstration data could not be loaded</h1>
          <p className="text-sm text-muted-foreground">Check your connection and try again.</p>
          <Button onClick={() => setState("loading")}>Try again</Button>
        </div>
      </div>
    )
  }
  return (
    <Suspense fallback={<FullPageLoader />}>
      <DemoApp />
    </Suspense>
  )
}
